import { useState } from "react";
import { approveGeneralInvoice, deriveGeneralClientCode, InvoiceOverrideRequiredError } from "../utils/generalInvoiceRegistry";
import TaxTreatmentPanel from "./TaxTreatmentPanel";

const pkr = (value) => `PKR ${Math.round(Number(value || 0)).toLocaleString("en-PK")}`;

export default function GeneralInvoiceApprovalControls({ invoice, invoiceMonth }) {
  const [invoiceDate, setInvoiceDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [approved, setApproved] = useState(false);

  async function approve() {
    setSaving(true);
    setMessage("");
    try {
      const saved = await approveGeneralInvoice(invoice, invoiceMonth, invoiceDate);
      setMessage(`${saved.invoiceNumber} approved and recorded for ${saved.monthKey}.`);
      setApproved(true);
    } catch (error) {
      if (error instanceof InvoiceOverrideRequiredError) {
        const { invoiceNumber, existingAmount, newAmount } = error.details;
        const confirmed = window.confirm(
          `${invoiceNumber} is already approved for ${pkr(existingAmount)}.\n\n` +
          `The recalculated figures now come to ${pkr(newAmount)}.\n\n` +
          `This will permanently override the existing approved invoice with the new amount. This cannot be undone. Continue?`
        );
        if (confirmed) {
          const reason = (window.prompt("Briefly describe why this invoice is being corrected (required):") || "").trim();
          if (!reason) {
            setMessage("Override cancelled — a reason is required.");
          } else {
            try {
              const saved = await approveGeneralInvoice(invoice, invoiceMonth, invoiceDate, { force: true, correctionReason: reason });
              setMessage(`${saved.invoiceNumber} corrected and re-approved for ${saved.monthKey}.`);
              setApproved(true);
            } catch (retryError) {
              setMessage(retryError.message || "Override failed.");
            }
          }
        } else {
          setMessage("Override cancelled — the existing approved invoice is unchanged.");
        }
      } else {
        setMessage(error.message || "Invoice approval failed.");
      }
    } finally {
      setSaving(false);
    }
  }

  const normalized = invoice.type === "service" || invoice.type === "ud-payroll" || invoice.type === "gsolar-invoice"
    ? {
        serviceChargeSubtotal: invoice.serviceAmount ?? invoice.serviceCharges ?? invoice.serviceChargeSum ?? 0,
        gstAmount: invoice.gst,
        grossAmount: invoice.grandTotal,
        recoveryBase: null
      }
    : {
        serviceChargeSubtotal: 0,
        gstAmount: 0,
        grossAmount: invoice.summary?.grandTotal ?? invoice.grandTotal ?? 0,
        recoveryBase: null
      };

  return (
    <div style={{ marginTop: 12 }}>
      <TaxTreatmentPanel clientCode={deriveGeneralClientCode(invoice.company)} normalized={normalized} />
      <div className="field" style={{ marginTop: 8, maxWidth: 220 }}>
        <label>Invoice Date</label>
        <input className="input" type="date" value={invoiceDate} disabled={approved}
          onChange={(event) => setInvoiceDate(event.target.value)} />
      </div>
      <button className="btn btn-success" type="button" style={{ marginTop: 8 }}
        disabled={saving || approved || !invoiceMonth?.trim()} onClick={approve}>
        {approved ? "Approved" : saving ? "Saving approval…" : "Approve & Save Invoice"}
      </button>
      {!invoiceMonth?.trim() && !approved && (
        <p className="page-copy" role="status" style={{ marginTop: 6 }}>Enter an Invoice Month above before approving.</p>
      )}
      {message && <p className="page-copy" role="status" style={{ marginTop: 10 }}>{message}</p>}
    </div>
  );
}
